import {
  ReloadIcon,
  ExclamationTriangleIcon,
  PaperPlaneIcon,
} from "@radix-ui/react-icons"
import clsx from "clsx"
import { motion } from "framer-motion"
import { Button } from "~/components/ui/button"
import { Label } from "~/components/ui/label"
import { Input } from "~/components/ui/input"
import { Form, useActionData, useNavigation } from "@remix-run/react"
import { contactFormSchema } from "~/schema/contact"
import { useForm, getFormProps, getInputProps } from "@conform-to/react"
import { parseWithZod, getZodConstraint } from "@conform-to/zod"

export const ContactForm = () => {
  const actionData = useActionData()
  const navigation = useNavigation()
  const [contactForm, contactFormFields] = useForm({
    constraint: getZodConstraint(contactFormSchema),
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: contactFormSchema })
    },
  })

  return (
    <Form
      method="POST"
      replace
      preventScrollReset
      className="mx-auto mt-2 flex w-full max-w-lg flex-col items-center justify-start rounded-xl border border-gray-300 bg-white shadow-md shadow-gray-100"
      {...getFormProps(contactForm)}
    >
      <div className="flex w-full flex-col p-6">
        <p className="text-black-900 mb-6 text-left font-sans text-2xl font-bold">
          Contact Us
        </p>
        <div className="flex w-full flex-col gap-y-6">
          <div className="flex w-full flex-col gap-y-2">
            <Label
              htmlFor={contactFormFields.name.id}
              className="text-black-900"
            >
              Your Name*
            </Label>
            <Input
              placeholder="Your Name"
              className="bg-white"
              {...getInputProps(contactFormFields.name, {
                type: "text",
              })}
            />
            <div
              className={clsx(
                contactFormFields.name.errors ? "" : "hidden",
                "flex items-start justify-start text-red-600/95",
              )}
            >
              <ExclamationTriangleIcon className="mr-2 size-6 shrink-0" />
              <p id={contactFormFields.name.errorId} className="text-left">
                {contactFormFields.name.errors}
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-y-2">
            <Label
              htmlFor={contactFormFields.email.id}
              className="text-black-900"
            >
              Email Address*
            </Label>
            <Input
              placeholder="Email"
              className="bg-white"
              {...getInputProps(contactFormFields.email, {
                type: "email",
              })}
            />
            <div
              className={clsx(
                contactFormFields.email.errors ? "" : "hidden",
                "flex items-start justify-start text-red-600/95",
              )}
            >
              <ExclamationTriangleIcon className="mr-2 size-6 shrink-0" />
              <p id={contactFormFields.email.errorId} className="text-left">
                {contactFormFields.email.errors}
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-y-2">
            <Label
              htmlFor={contactFormFields.mobileNumber.id}
              className="text-black-900"
            >
              Mobile Number (optional)
            </Label>
            <Input
              placeholder="Mobile Number (optional)"
              className="bg-white"
              {...getInputProps(contactFormFields.mobileNumber, {
                type: "text",
              })}
            />
            <div
              className={clsx(
                contactFormFields.mobileNumber.errors ? "" : "hidden",
                "flex items-start justify-start text-red-600/95",
              )}
            >
              <ExclamationTriangleIcon className="mr-2 size-6 shrink-0" />
              <p
                id={contactFormFields.mobileNumber.errorId}
                className="text-left"
              >
                {contactFormFields.mobileNumber.errors}
              </p>
            </div>
          </div>

          {actionData?.status === "success" ? (
            <div className="text-black-900 flex items-start justify-center gap-x-4">
              <span className="text-2xl">✅</span>
              <p className="text-left font-sans text-base font-medium">
                {`Your enquiry is sent! We'll be in touch shortly. Thank you.`}
              </p>
            </div>
          ) : (
            <Button
              type="submit"
              className="flex gap-x-2 drop-shadow-md"
              disabled={navigation.state === "submitting"}
              variant="default"
            >
              {navigation.state === "submitting" ? (
                <span className="mr-2 animate-spin text-2xl">🔄</span>
              ) : null}
              <span>Send</span>
              <span className="text-2xl">✉️</span>
            </Button>
          )}
        </div>
      </div>
    </Form>
  )
}
